import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';
import * as math from 'mathjs';

ChartJS.register(
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const DifferentialEquationsCalculator = () => {
  const [inputData, setInputData] = useState({
    equation: '',
    initialX: 0,
    initialY: 0,
    stepSize: 0.1,
    numSteps: 100
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const solveDifferentialEquation = () => {
    const { equation, initialX, initialY, stepSize, numSteps } = inputData;

    // Validate input
    if (!equation || !initialX || !initialY || !stepSize || !numSteps) {
      setError('Please fill in all fields');
      return;
    }

    try {
      // Parse the differential equation
      const parsedEquation = math.parse(equation);

      // Euler method to solve the ODE
      const xValues = [parseFloat(initialX)];
      const yValues = [parseFloat(initialY)];
      const h = parseFloat(stepSize);
      const n = parseInt(numSteps);

      for (let i = 1; i <= n; i++) {
        const x = xValues[i - 1];
        const y = yValues[i - 1];
        const dydx = math.evaluate(parsedEquation, { x, y });
        xValues.push(x + h);
        yValues.push(y + h * dydx);
      }

      // Create visualization data
      const chartData = {
        labels: xValues,
        datasets: [
          {
            label: 'Solution',
            data: yValues,
            borderColor: 'rgba(75, 192, 192, 1)',
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
            fill: false
          }
        ]
      };

      setResults({
        chartData
      });
    } catch (e) {
      setError('Please enter a valid differential equation');
    }
  };

  const exampleData = {
    equation: 'dy/dx = -y + x',
    initialX: 0,
    initialY: 1,
    stepSize: 0.1,
    numSteps: 100
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Differential Equations Calculator | ODE Solver</title>
          <meta name="description" content="Solve first-order ordinary differential equations using the Euler method. This calculator helps in mathematical analysis and numerical methods." />
          <meta name="keywords" content="differential equations calculator, ODE solver, Euler method, numerical methods" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Differential Equations Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Solve first-order ordinary differential equations using the Euler method. This calculator helps in mathematical analysis and numerical methods.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Differential Equation (dy/dx):</label>
              <input
                type="text"
                name="equation"
                value={inputData.equation}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter differential equation (e.g., -y + x)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial X:</label>
              <input
                type="number"
                name="initialX"
                value={inputData.initialX}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter initial X value"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Y:</label>
              <input
                type="number"
                name="initialY"
                value={inputData.initialY}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter initial Y value"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Step Size (h):</label>
              <input
                type="number"
                name="stepSize"
                value={inputData.stepSize}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter step size"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Steps:</label>
              <input
                type="number"
                name="numSteps"
                value={inputData.numSteps}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter number of steps"
              />
            </div>
          </div>

          <button
            onClick={solveDifferentialEquation}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Solve Differential Equation
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Line
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: false,
                          title: {
                            display: true,
                            text: 'y'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'x'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Solution of Differential Equation'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for solving a differential equation:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Differential Equation: {exampleData.equation}</li>
                <li>Initial X: {exampleData.initialX}</li>
                <li>Initial Y: {exampleData.initialY}</li>
                <li>Step Size: {exampleData.stepSize}</li>
                <li>Number of Steps: {exampleData.numSteps}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Parse the differential equation</li>
                <li>Use the Euler method to approximate the solution</li>
                <li>Generate x and y values for the solution</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Differential Equations Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Differential Equation:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the differential equation (e.g., dy/dx = -y + x)</li>
                </ul>
              </li>
              <li><strong>Enter Initial Conditions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the initial values for x and y</li>
                </ul>
              </li>
              <li><strong>Enter Step Size and Number of Steps:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the step size (h) and number of steps for the Euler method</li>
                </ul>
              </li>
              <li><strong>Click Solve:</strong> The calculator will approximate the solution using the Euler method</li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the approximated solution</li>
                  <li>View the visualization of the solution</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure the differential equation is valid and uses 'x' and 'y' as variables</li>
                <li>Useful for mathematical analysis and numerical methods</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Differential Equations <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Differential equations are equations that relate a function with one or more of its derivatives. They are used to model a wide range of phenomena in physics, engineering, biology, and more. Key points:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Modeling Change:</strong> Describe how quantities change over time or space</li>
              <li><strong>Types:</strong> Ordinary differential equations (ODEs) and partial differential equations (PDEs)</li>
              <li><strong>Use Cases:</strong> Population growth, heat transfer, fluid dynamics, and more</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your input values and equations</li>
              <li><strong>Documentation:</strong> Record your calculations</li>
              <li><strong>Validation:</strong> Verify results with other tools or manual calculations</li>
              <li><strong>Implementation:</strong> Ensure the differential equation is valid and uses 'x' and 'y' as variables</li>
            </ul>
            <p className="italic mt-4">
              Note: Always verify your calculations, especially when using differential equations for critical decision-making.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DifferentialEquationsCalculator;